import get from 'lodash/get';
import api from 'services/api';
import { trackEvent } from 'tracking/GA';
import { ACTIONS, CATEGORIES } from 'tracking/GA/constants';
import { closeModal } from 'store/modals/actions';
import { MODAL_RUN_USER_AGENT, MODAL_RUN_CLOUD_AGENT, MODAL_AGENT_DETAIL } from 'store/modals/modals';
import { selectActiveWorkspaces } from 'store/Workspace/selectors';
import { setError } from 'store/errors/actions';
import * as types from './types';
import {
  selectTriggerAgentWs, selectTriggerResolveHostname,
  selectAgentList, selectAgentsSelected, selectLastSelected,
  selectTriggerAgentId, SelectShowCloudAgents, selectAssetsTags, selectVulnsTags,
  selectServicesTags,
  selectTriggerAgentTools,
  selectAgentExecutionSelected,
  selectAgentExecutions,
  selectTriggerCloudAgentWs,
  selectAgentExecutionType,
  selectTriggerMinSeverity,
  selectTriggerMaxSeverity
} from './selectors';
import { selectQueryParam } from 'store/Filters/selectors';
import { clearOrderBy, setOrderBy, setPageNumber } from 'store/Filters/actions';
import { cloneDeep } from 'lodash';
import sanitizeByMetadata from 'utils/sanitizeParameters';

export const resetState = () => (dispatch) => dispatch({ type: types.AGENTS_RESET_STATE });

function fetching () {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_FETCHING });
  };
}

const DEFAULT_RUN_ERROR_MESSAGE = 'There was an error, please try again.';

const flattenErrorValue = (value) => {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(flattenErrorValue).filter(Boolean).join(', ');
  if (value && typeof value === 'object') return Object.values(value).map(flattenErrorValue).filter(Boolean).join(', ');
  return '';
};

function buildRunErrorMessage (error, fallback = DEFAULT_RUN_ERROR_MESSAGE) {
  const { status, limits, ...rest } = error || {};
  return flattenErrorValue(rest.message || rest) || fallback;
}

function dispatchRunError (dispatch, e) {
  dispatch({ type: types.AGENTS_RUN_FAILED });
  dispatch(setError(buildRunErrorMessage(e)));
}

function somethingWentWrong (error) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_FAIL, errorMessage: buildRunErrorMessage(error), errors: error });
  };
}

export function getUserAgents () {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch(fetching());
      const queryParam = selectQueryParam('agents', state);
      const response = await api.agents.fetchUserAgents(queryParam);
      const userAgents = response.rows.map((el) => el);
      dispatch({ type: types.AGENTS_GET_USER_AGENTS, userAgents, count: response.count });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function getCloudAgents () {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch(fetching());
      const queryParam = selectQueryParam('cloudAgents', state);
      const response = await api.agents.fetchCloudAgents(queryParam);
      const cloudAgents = response.rows.map((el) => el);
      dispatch({ type: types.AGENTS_GET_CLOUD_AGENTS, cloudAgents, count: response.count });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function getToken () {
  return async (dispatch) => {
    try {
      const responseSession = await api.faraday.getSession();

      const fd = new FormData();
      fd.set('csrf_token', responseSession.csrf_token);

      const responseToken = await api.agents.fetchToken(fd);
      dispatch({
        type: types.AGENTS_GET_TOKEN, token: responseToken.token, tokenExpiresIn: responseToken.expires_in, tokenDuration: responseToken.total_duration
      });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function getAgent (id) {
  return async (dispatch, getState) => {
    try {
      const cloudAgent = SelectShowCloudAgents(getState());
      dispatch({ type: types.GET_USER_AGENT_REQUEST });
      let response = '';
      if (cloudAgent) {
        response = await api.agents.fetchCloudAgentData(id);
        dispatch({ type: types.GET_USER_CLOUD_AGENT_SUCCESS, id, agent: response });
      } else {
        response = await api.agents.fetchAgentData(id);
        dispatch({ type: types.GET_USER_AGENT_SUCCESS, id, agent: response });
      }
    } catch (e) {
      dispatch({ type: types.GET_USER_AGENT_FAIL, errorMessage: get(e, 'message', 'There was an error, please try again.') });
    }
  };
}

export function setAgentId (id) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_SET_ID, id });
  };
}

export const toggleAgentsTab = (bool) => (dispatch) => {
  dispatch({ type: types.TOGGLE_AGENTS_TAB, payload: bool });
};

export function runUserAgentTool (toolName, toolParams, toolId) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const agentId = selectTriggerAgentId(state);
      const workspaces = selectTriggerAgentWs(state, toolId);
      const min_severity = selectTriggerMinSeverity(state, toolId);
      const max_severity = selectTriggerMaxSeverity(state, toolId);
      const resolveHostname = selectTriggerResolveHostname(state, toolId);

      dispatch(fetching());

      const toolParamsClone = cloneDeep(toolParams);
      // sanitize args using executor metadata if available
      const executors = selectTriggerAgentTools(state);
      const executor = executors.find((t) => t.id === toolId) || {};
      const paramsMeta = executor.parameters_metadata || {};
      const extraAllowed = [];
      const sanitizedArgs = sanitizeByMetadata(toolParamsClone, paramsMeta, extraAllowed);

      const payload = {
        min_severity,
        max_severity,
        resolve_hostname: resolveHostname,
        workspaces_names: workspaces,
        executor_data: {
          agent_id: agentId,
          executor: toolName,
          args: sanitizedArgs
        },

        vuln_tag: toolParams.vuln_tag,
        service_tag: toolParams.service_tag,
        host_tag: toolParams.host_tag
      };
      await api.agents.runUserAgentTool(agentId, payload);
      dispatch(trackEvent(CATEGORIES.agent, ACTIONS.runAgent.name, ACTIONS.runAgent.label, agentId));

      dispatch(getUserAgents());
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SET_SUCCESS, message: `${toolName} has been started` });
      dispatch(setTagsEntity('assetsTags', false));
      dispatch(setTagsEntity('vulnsTags', false));
      dispatch(setTagsEntity('servicesTags', false));
    } catch (e) {
      dispatch(setError(buildRunErrorMessage(e)));
    }
  };
}

export function setSelectedCloudAgent (cloudAgent) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_SELECT_CLOUD_AGENT, cloudAgent });
  };
}

export function runCloudAgent (args) {
  return async (dispatch, getState) => {
    try {
      const agentId = selectTriggerAgentId(getState());
      const workspaces = selectTriggerCloudAgentWs(getState());
      const min_severity = selectTriggerMinSeverity(getState());
      const max_severity = selectTriggerMaxSeverity(getState());
      const resolveHostname = selectTriggerResolveHostname(getState());
      const argsClone = cloneDeep(args);
      // sanitize cloud args by cloud agent params if available
      const state = getState();
      let cloudAgent = get(state, 'agents.selectedCloudAgent', null);
      if (!cloudAgent) {
        const cloudAgents = get(state, 'agents.cloudAgents', []);
        cloudAgent = cloudAgents.find((ca) => ca.id === agentId) || null;
      }
      const cloudParamsMeta = cloudAgent?.params || {};
      const sanitizedArgs = sanitizeByMetadata(argsClone, cloudParamsMeta, []);

      dispatch(fetching());
      const payload = {
        workspaces,
        args: sanitizedArgs,
        min_severity,
        max_severity,
        resolve_hostname: resolveHostname,
        host_tag: args.host_tag,
        vuln_tag: args.vuln_tag,
        service_tag: args.service_tag
      };

      await api.agents.runCloudAgent(agentId, payload);
      dispatch(trackEvent(CATEGORIES.cloudAgent, ACTIONS.runCloudAgent.name, ACTIONS.runCloudAgent.label, agentId));
      dispatch(getCloudAgents());
      dispatch(closeModal(MODAL_RUN_CLOUD_AGENT));
      dispatch(closeModal(MODAL_AGENT_DETAIL));
      dispatch({ type: types.AGENTS_SUCCESS });
    } catch (e) {
      dispatchRunError(dispatch, e);
    }
  };
}

export function runCloudAgentTool (wsSelected, toolParams, tags) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const agentId = selectTriggerAgentId(state);
      const assetsTags = selectAssetsTags(state);
      const vulnsTags = selectVulnsTags(state);
      const servicesTags = selectServicesTags(state);
      dispatch(fetching());
      const workspaces = wsSelected.map(w => w.primary);
      const parsedTags = tags ? tags.split(',').map((t) => t.trim()) : [];
      const newTags = [...new Set(parsedTags)];

      const payload = {
        workspaces,
        args: toolParams,
        vuln_tag: vulnsTags ? newTags : [],
        service_tag: servicesTags ? newTags : [],
        host_tag: assetsTags ? newTags : []
      };

      await api.agents.runCloudAgent(agentId, payload);

      dispatch(getCloudAgents());
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SUCCESS });
      dispatch(setTagsEntity('assetsTags', false));
      dispatch(setTagsEntity('vulnsTags', false));
      dispatch(setTagsEntity('servicesTags', false));
    } catch (e) {
      dispatchRunError(dispatch, e);
    }
  };
}

export function resetWizard () {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_RESET });
  };
}

export function addTool (toolName) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_ADD_TOOL, tool: { name: toolName, variables: [] } });
  };
}

export function removeTool (tool) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_REMOVE_TOOL, tool });
  };
}

export function setFieldsTool (toolName, fields) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_SET_FIELDS_TOOL, toolName, fields });
  };
}

export function setAgentExecutorSeleted (agent) {
  return async (dispatch) => {
    dispatch({ type: types.AGENT_EXECUTIONS_SET_SELECTED, agent });
  };
}

export function getManifest () {
  return async (dispatch) => {
    try {
      const data = await api.manage.fetchManifest();
      dispatch({ type: types.AGENTS_GET_MANIFEST, data });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export const setAgent = (data) => (dispatch) => {
  dispatch({ type: types.SET_NEW_AGENT, data });
};

export function clearAgent () {
  return (dispatch) => {
    dispatch({ type: types.RESET_NEW_AGENT });
  };
}

export function setTriggerAgentWs (workspaces, executor) {
  return (dispatch) => {
    const newExecutor = executor;
    Object.assign(newExecutor.parameters_data, { workspaces_names: workspaces });
    dispatch({ type: types.AGENTS_RUN_SET_WORKSPACES, workspaces, newExecutor });
  };
}

export function addTriggerAgentWs (ws, executor) {
  return (dispatch, getState) => {
    const addedWorkspaces = selectTriggerAgentWs(getState(), executor?.id);
    const allWorkspaces = selectActiveWorkspaces(getState());

    const newWs = allWorkspaces.find((workspace) => workspace.name === ws)?.name;
    if (newWs) {
      const newWorkspaces = [
        ...addedWorkspaces.filter((workspace) => workspace !== ws),
        newWs
      ];
      dispatch(setTriggerAgentWs(newWorkspaces, executor));
    }
  };
}

export function removeTriggerAgentWs (ws, executor) {
  return (dispatch, getState) => {
    const addedWorkspaces = selectTriggerAgentWs(getState(), executor?.id);
    const newWorkspaces = addedWorkspaces.filter((w) => w !== ws);
    dispatch(setTriggerAgentWs(newWorkspaces, executor));
  };
}

export function setSeverityRange (severityRange) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_RUN_SET_SEVERITY_RANGE, severityRange });
  };
}

export function setResolveHostname (resolveHostname) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_RUN_SET_RESOLVE_HOSTNAME, resolveHostname });
  };
}

export function setResolveHostnameInDetail (resolveHostname, executor) {
  return (dispatch) => {
    const newExecutor = executor;
    Object.assign(newExecutor.parameters_data, { resolve_hostname: resolveHostname });
    dispatch({ type: types.AGENTS_DETAIL_SET_RESOLVE_HOSTNAME, newExecutor });
  };
}

export function setSeverityRangeInDetail (min, max, executor) {
  return (dispatch) => {
    const newExecutor = executor;
    Object.assign(newExecutor.parameters_data, { min_severity: min, max_severity: max });
    dispatch({ type: types.AGENTS_DETAIL_SET_SEVERITY_RANGE, newExecutor });
  };
}

// Click Logic

const addDeleteController = (agents, agentList) => {
  const allAgentsAreSelected = agents.every((testAgent) => agentList.some((agent) => agent.id === testAgent.id));

  if (allAgentsAreSelected) return [types.AGENT_UNSELECTED, agents];
  return [types.AGENT_SELECTED, agents];
};

const selectCalculator = (e, agent, agentList, areAgentsSelected) => (dispatch, getState) => {
  const pivot = selectLastSelected(getState());
  const index = agentList.findIndex((el) => el.id === agent.id);
  const agentsSelected = selectAgentsSelected(getState());

  dispatch({ type: types.NEW_PIVOT, payload: index });
  if (e.shiftKey && pivot !== -1 && areAgentsSelected) {
    const start = Math.min(pivot, index);
    const end = Math.max(pivot, index) + 1;
    const agents = agentList.slice(start, end);
    const [type, payload] = addDeleteController(agents, agentsSelected);
    return dispatch({ type, payload });
  }
  const [type, payload] = addDeleteController([agent], agentsSelected);
  return dispatch({ type, payload });
};

export const selectRow = (e, agent) => (dispatch, getState) => {
  const allAgents = selectAgentList(getState());
  const agentSelected = selectAgentsSelected(getState());
  dispatch(selectCalculator(e, agent, allAgents, agentSelected.length > 0));
};

export const unSelectAll = () => (dispatch) => dispatch({ type: types.UNSELECT_ALL_AGENT });

export const selectAllAgents = () => (dispatch, getState) => {
  const agentsList = selectAgentList(getState());
  const agentsSelected = selectAgentsSelected(getState());

  if (agentsSelected.length === agentsList.length) return dispatch(unSelectAll());

  return dispatch({ type: types.SELECT_ALL_AGENT, agentsList });
};

export function setTagsEntity (entity, value) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_RUN_SET_TAGS_ENTITY, entity, value });
  };
}
export const selectAllAgentExecutions = () => (dispatch, getState) => {
  const agentExecutionsList = selectAgentExecutions(getState());
  return dispatch({ type: types.SELECT_ALL_AGENT_EXECUTIONS, agentExecutionsList });
};

export function changeStateAgent (agent) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const agentId = selectTriggerAgentId(state);
      dispatch(fetching());
      const payload = {
        name: agent?.name,
        active: !agent?.active
      };
      await api.agents.changeStateAgent(agent.id, payload);
      dispatch(trackEvent(CATEGORIES.agent, ACTIONS.runAgent.name, ACTIONS.runAgent.label, agentId));
      dispatch(getUserAgents());
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SUCCESS });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

// Cloud Agents (Security Scanners) cannot be deleted - this only ever runs for Runner agents.
export function removeSelectedAgents () {
  return async (dispatch, getState) => {
    try {
      const agentsSelected = selectAgentsSelected(getState());
      if (!agentsSelected.length) return;
      const [firstAgent] = agentsSelected;

      dispatch(fetching());

      const ids = agentsSelected.map((agent) => agent.id);
      await api.agents.removeAgents(ids);

      dispatch(trackEvent(CATEGORIES.agent, ACTIONS.deleteAgent.name, ACTIONS.deleteAgent.label, firstAgent.id));
      dispatch(getUserAgents());
      dispatch(unSelectAll());
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function removeAgent (agent) {
  return async (dispatch) => {
    try {
      dispatch(fetching());
      await api.agents.removeAgents([agent.id]);
      dispatch(trackEvent(CATEGORIES.agent, ACTIONS.deleteAgent.name, ACTIONS.deleteAgent.label, agent.id));
      dispatch(getUserAgents());
      dispatch(unSelectAll());
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function setPageUserAgents (pageNumber) {
  return (dispatch) => {
    dispatch(setPageNumber('agents', pageNumber));
    dispatch(getUserAgents());
  };
}

export function setPageCloudAgents (pageNumber) {
  return (dispatch) => {
    dispatch(setPageNumber('cloudAgents', pageNumber));
    dispatch(getCloudAgents());
  };
}

export function setPageAgentExecutions (pageNumber, entity) {
  return (dispatch) => {
    dispatch(setPageNumber('agentExecutions', pageNumber));
    dispatch(getAgentsExecutions(entity));
  };
}

export function setToolSelected (tool) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_SET_TOOL_SELECTED, tool });
  };
}

export const saveAgentToolData = (id, data) => async (dispatch, getState) => {
  try {
    const state = getState();
    const workspaces = selectTriggerAgentWs(state, id);
    const min_severity = selectTriggerMinSeverity(state, id);
    const max_severity = selectTriggerMaxSeverity(state, id);
    const resolveHostname = selectTriggerResolveHostname(state, id);
    const assetsTags = selectAssetsTags(state);
    const vulnsTags = selectVulnsTags(state);
    const servicesTags = selectServicesTags(state);
    const agentId = selectTriggerAgentId(state);

    // Build parameters_data by whitelisting only fields defined in executor.parameters_metadata
    const executors = selectTriggerAgentTools(state);
    const executor = executors.find((t) => t.id === id) || {};
    const paramsMeta = executor.parameters_metadata || {};

    const extraAllowed = ['min_severity', 'max_severity', 'resolve_hostname', 'workspaces_names', 'vuln_tag', 'service_tag', 'host_tag'];
    const parameters_data = sanitizeByMetadata(data, paramsMeta, extraAllowed);

    // ensure we include/seal other UI-provided values (override if needed)
    parameters_data.min_severity = min_severity;
    parameters_data.max_severity = max_severity;
    parameters_data.resolve_hostname = resolveHostname;
    parameters_data.workspaces_names = workspaces;
    parameters_data.vuln_tag = vulnsTags.map((t) => t?.name ?? t);
    parameters_data.service_tag = servicesTags.map((t) => t?.name ?? t);
    parameters_data.host_tag = assetsTags.map((t) => t?.name ?? t);
    if (id) {
      await api.agents.saveToolData({ executor_id: id, parameters_data });
      dispatch({ type: types.SAVE_AGENT_TOOL_DATA_SUCCESS });
      dispatch(getAgent(agentId));
    }
  } catch (e) {
    dispatch(somethingWentWrong(e));
  }
};

export const clearSuccessMessage = () => (dispatch) => {
  dispatch({ type: types.AGENTS_CLEAR_SUCCESS });
};

export const resetDetail = () => (dispatch) => {
  dispatch({ type: types.AGENTS_RESET_DETAIL });
};

export const saveAndRun = (id, data, toolName) => async (dispatch) => {
  try {
    await dispatch(saveAgentToolData(id, data));
    dispatch(runUserAgentTool(toolName, data, id));
  } catch (e) {
    dispatch(somethingWentWrong(e));
  }
};

export function setTagsAgent (tags) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_SET_TAG, tags });
  };
}

export function getAgentsExecutions () {
  return async (dispatch, getState) => {
    try {
      dispatch(fetching());
      const executionType = selectAgentExecutionType(getState());
      const filterEntity = executionType === 'agent_executions' ? 'agentExecutions' : 'cloudAgentExecutions';
      const queryParam = selectQueryParam(filterEntity, getState());
      const response = await api.agents.getAgentsExecutions(executionType, queryParam);
      dispatch({ type: types.GET_AGENT_EXECUTIONS, response });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function getResultExecutions (commandId, workspaceName) {
  return async (dispatch, getState) => {
    try {
      dispatch(fetching());
      const vulns = await api.agents.getResultExecutions('vulns', commandId);
      const services = await api.agents.getResultExecutions('services', commandId);
      const hosts = await api.agents.getResultExecutions('hosts', commandId);

      const results = {
        vulns: vulns?.count,
        services: services?.count,
        hosts: hosts?.count,
        workspaceName
      };

      dispatch({ type: types.GET_RESULT_EXECUTIONS, results });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function getResultsExecutions (workspacesCommands) {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.RESET_RESULT_EXECUTIONS });
      workspacesCommands.map(({ command_id, workspace_name }) => dispatch(getResultExecutions(command_id, workspace_name)));
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function deleteAgentExecutions (entity) {
  return async (dispatch, getState) => {
    try {
      dispatch(fetching());
      const state = getState();
      const agentsExecutionsSelected = selectAgentExecutionSelected(state).map((ae) => ae.id);

      const data = {
        ids: agentsExecutionsSelected
      };

      await api.agents.deleteAgentsExecutions(entity, data);
      dispatch(unselectAllAgentExecutions());
      dispatch(getAgentsExecutions(entity));
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export const multipleAgentExecutionSetSelected = (agent) => (dispatch) => dispatch({ type: types.MULTIPLE_AGENT_EXECUTIONS_SET_SELECTED, agent });

export const unselectAllAgentExecutions = () => (dispatch) => dispatch({ type: types.UNSELECT_ALL_AGENTS_EXECUTIONS });

export const unselectAgentExecution = (agent) => (dispatch) => dispatch({ type: types.UNSELECT_AGENTS_EXECUTIONS, agent });

export const setAgentExecutionsType = (agentType) => (dispatch) => {
  dispatch(setPageNumber('agentExecutions', 1));
  dispatch({ type: types.SET_AGENT_EXECUTIONS_TYPE, agentType });
};

export function runAgentFromLogs () {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const executionSelected = selectAgentExecutionSelected(state);
      const type = selectAgentExecutionType(state);
      const agentId = get(executionSelected, '[0].agent_id', '');
      const executorData = get(executionSelected, '[0].parameters_data.executor_data', {});
      const workspacesCommands = get(executionSelected, '[0].parameters_data.workspaces_commands', []);

      const executors = selectTriggerAgentTools(state);
      const executor = executors.find((t) => t.name === executorData.executor || t.id === executorData.executor_id) || {};
      const paramsMeta = executor.parameters_metadata || {};
      const sanitizedArgs = Object.keys(paramsMeta).length ? sanitizeByMetadata(executorData.args || {}, paramsMeta, []) : (executorData.args || {});

      dispatch(fetching());

      const payload = {
        workspaces_names: workspacesCommands.map((e) => e.workspace_name),
        executor_data: {
          ...executorData,
          agent_id: agentId,
          args: sanitizedArgs
        }
      };

      await api.agents.runUserAgentTool(agentId, payload);

      dispatch(getAgentsExecutions(type));
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SUCCESS });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function runCloudAgentFromLogs () {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const executionSelected = selectAgentExecutionSelected(state);
      const type = selectAgentExecutionType(state);
      const agentId = get(executionSelected, '[0].cloud_agent_id', '');
      const data = get(executionSelected, '[0].parameters_data.args', {});
      const workspaces = get(executionSelected, '[0].parameters_data.workspaces', []);

      let cloudAgent = get(state, 'agents.selectedCloudAgent', null);
      if (!cloudAgent || cloudAgent.id !== agentId) {
        const cloudAgents = get(state, 'agents.cloudAgents', []);
        cloudAgent = cloudAgents.find((ca) => ca.id === agentId) || cloudAgent;
      }
      const cloudParamsMeta = cloudAgent?.params || {};
      const sanitizedArgs = Object.keys(cloudParamsMeta).length ? sanitizeByMetadata(data, cloudParamsMeta, []) : (data || {});

      dispatch(fetching());
      const payload = {
        args: sanitizedArgs,
        workspaces
      };
      await api.agents.runCloudAgent(agentId, payload);

      dispatch(getAgentsExecutions(type));
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SUCCESS });
    } catch (e) {
      dispatchRunError(dispatch, e);
    }
  };
}

export function setTriggerCloudAgentWs (workspaces) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_SET_WORKSPACES_CLOUD, workspaces });
  };
}

export function addTriggerCloudAgentWs (ws) {
  return (dispatch, getState) => {
    const addedWorkspaces = selectTriggerCloudAgentWs(getState());
    const allWorkspaces = selectActiveWorkspaces(getState());

    const newWs = allWorkspaces.find((workspace) => workspace.name === ws)?.name;
    if (newWs) {
      const newWorkspaces = [
        ...addedWorkspaces.filter((workspace) => workspace !== ws),
        newWs
      ];

      dispatch(setTriggerCloudAgentWs(newWorkspaces));
    }
  };
}

export function removeTriggerCloudAgentWs (ws) {
  return (dispatch, getState) => {
    const addedWorkspaces = selectTriggerCloudAgentWs(getState());
    const newWorkspaces = addedWorkspaces.filter((w) => w !== ws);
    dispatch(setTriggerCloudAgentWs(newWorkspaces));
  };
}

export const saveCloudAgent = (id, data) => async (dispatch, getState) => {
  try {
    const state = getState();
    const workspaces = selectTriggerCloudAgentWs(state);
    const min_severity = selectTriggerMinSeverity(state);
    const max_severity = selectTriggerMaxSeverity(state);
    const resolveHostname = selectTriggerResolveHostname(state);
    const assetsTags = selectAssetsTags(state);
    const vulnsTags = selectVulnsTags(state);
    const servicesTags = selectServicesTags(state);
    const agentId = selectTriggerAgentId(state);

    // Build parameters_data for cloud agent by whitelisting using cloud agent params
    let cloudAgent = get(state, 'agents.selectedCloudAgent', null);
    if (!cloudAgent || cloudAgent.id !== id) {
      const cloudAgents = get(state, 'agents.cloudAgents', []);
      cloudAgent = cloudAgents.find((ca) => ca.id === id) || cloudAgent;
    }
    const cloudParamsMeta = cloudAgent?.params || {};
    const extraCloudAllowed = ['min_severity', 'max_severity', 'resolve_hostname', 'workspaces', 'vuln_tag', 'service_tag', 'host_tag'];
    const parameters_data = sanitizeByMetadata(data, cloudParamsMeta, extraCloudAllowed);

    parameters_data.min_severity = min_severity;
    parameters_data.max_severity = max_severity;
    parameters_data.resolve_hostname = resolveHostname;
    parameters_data.workspaces = workspaces;
    parameters_data.vuln_tag = vulnsTags.map((t) => t?.name ?? t);
    parameters_data.service_tag = servicesTags.map((t) => t?.name ?? t);
    parameters_data.host_tag = assetsTags.map((t) => t?.name ?? t);

    await api.agents.saveCloudAgents({ cloud_agent_id: id, parameters_data });
    dispatch({ type: types.SAVE_AGENT_TOOL_DATA_SUCCESS });
    dispatch(getAgent(agentId));
  } catch (e) {
    dispatch(somethingWentWrong(e));
  }
};

export const setOrderByUserAgents = (sorting) => (dispatch) => {
  dispatch(setOrderBy('agents', sorting));
  dispatch(getUserAgents());
};

export const setOrderByCloudAgents = (sorting) => (dispatch) => {
  dispatch(setOrderBy('cloudAgents', sorting));
  dispatch(getCloudAgents());
};

export const setOrderByAgentsExecutions = (sorting = [], entity) => (dispatch) => {
  if (sorting.length > 0) dispatch(setOrderBy('agentExecutions', sorting));
  else dispatch(clearOrderBy('agentExecutions'));
  dispatch(getAgentsExecutions(entity));
};

export const setAgentsEntity = (entity) => {
  return async (dispatch) => {
    dispatch({ type: types.SET_AGENTS_ENTITY, entity });
  };
};

// export function getAgentsFilters () {
//   return async (dispatch, getState) => {
//     dispatch(getDataStart());
//     let advancedFilterQueryParam = [];

//     try {
//       advancedFilterQueryParam = selectAdvancedFilterQueryParam(getState(), 'agents');
//     } catch (e) {
//       dispatch(setFilterError('agents', 'Syntax error. Please try again. For further help check our documentation'));
//     }

//     const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
//     const standardQueryParam = selectQueryParamFromFilters('agents', getState());
//     const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

//     try {
//       const response = await api.agents.getAgentByFilters(queryParam);
//       const agents = response.rows.map((reportRow) => reportRow.value);
//       const reportsCount = response.total_rows;

//       dispatch({ type: types.AGENTS_GET_USER_AGENTS, userAgents: agents, count: response.count });
//     } catch (error) {
//       dispatch(error('Failed to obtain agents.'));
//     }
//   };
// }

// export function getCloudAgentsFilters () {
//   return async (dispatch, getState) => {
//     dispatch(getDataStart());
//     let advancedFilterQueryParam = [];

//     try {
//       advancedFilterQueryParam = selectAdvancedExpandedQueryParam(getState(), 'agents');
//     } catch (e) {
//       dispatch(setFilterError('agents', 'Syntax error. Please try again. For further help check our documentation'));
//     }

//     const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
//     const standardQueryParam = selectQueryParam('cloudAgents', getState());
//     const queryParam = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;

//     try {
//       const response = await api.agents.getCloudAgentByFilters(queryParam);
//       const reportList = response.rows.map((reportRow) => reportRow.value);
//       const reportsCount = response.total_rows;

//       dispatch(getDataSuccess(reportList, reportsCount));
//     } catch (error) {
//       dispatch(error('Failed to obtain agents.'));
//     }
//   };
// }
